/**
 * useTravelBudget — budget row, expenses list, and summary for a trip.
 *
 * Consolidates three related queries (budget, expenses, summary) behind a
 * single hook so pages don't have to juggle three `useQuery`s and three
 * invalidations. Mutation callers get optimistic cache updates where it
 * matters (create/delete): the summary is invalidated on every change so
 * the totals bar stays honest.
 *
 * Renders as a no-op when the `budget` feature is disabled — pages
 * should read `featureEnabled` and hide their affordances rather than
 * rendering a disabled-looking panel.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type {
  TravelBudgetResponse,
  TravelExpenseResponse,
  TravelBudgetSummaryResponse,
} from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useTravelFeature } from './useTravelUserProfile';

export interface CreateExpenseVars {
  itemId?: string | null;
  eventId?: string | null;
  category?: string;
  description?: string | null;
  amountCents: number;
  currency: string;
  incurredAt: string;
  paidBy?: string | null;
  split?: { participants?: Array<{ name: string; share: number }>; paidBy?: string };
}

export function useTravelBudget(tripId: string | undefined) {
  const feature = useTravelFeature('budget');
  const queryClient = useQueryClient();
  const enabled = !!tripId && feature.enabled;

  const budgetQuery = useQuery({
    enabled,
    queryKey: tripId ? queryKeys.travel.budget(tripId) : queryKeys.travel.disabled('budget'),
    queryFn: () => travelApi.getTravelBudget(tripId!),
  });

  const expensesQuery = useQuery({
    enabled,
    queryKey: tripId ? queryKeys.travel.expenses(tripId) : queryKeys.travel.disabled('expenses'),
    queryFn: () => travelApi.listTravelExpenses(tripId!),
  });

  const summaryQuery = useQuery({
    enabled,
    queryKey: tripId
      ? queryKeys.travel.budgetSummary(tripId)
      : queryKeys.travel.disabled('budgetSummary'),
    queryFn: () => travelApi.getTravelBudgetSummary(tripId!),
  });

  const invalidateAll = () => {
    if (!tripId) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.travel.budget(tripId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.travel.expenses(tripId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.travel.budgetSummary(tripId) });
  };

  const updateBudgetMut = useMutation({
    mutationFn: (input: { homeCurrency?: string; totalCents?: number | null }) =>
      travelApi.updateTravelBudget(tripId!, input),
    onSuccess: (res) => {
      if (!tripId) return;
      queryClient.setQueryData<{ budget: TravelBudgetResponse | null } | undefined>(
        queryKeys.travel.budget(tripId),
        { budget: res.budget },
      );
      // Summary home currency can change — refetch.
      void queryClient.invalidateQueries({ queryKey: queryKeys.travel.budgetSummary(tripId) });
    },
  });

  const createExpenseMut = useMutation({
    mutationFn: (input: CreateExpenseVars) =>
      travelApi.createTravelExpense(tripId!, input as unknown as Record<string, unknown>),
    onSuccess: () => invalidateAll(),
  });

  const updateExpenseMut = useMutation({
    mutationFn: (vars: { expenseId: string; input: Partial<CreateExpenseVars> }) =>
      travelApi.updateTravelExpense(
        vars.expenseId,
        vars.input as unknown as Record<string, unknown>,
      ),
    onSuccess: () => invalidateAll(),
  });

  const deleteExpenseMut = useMutation({
    mutationFn: (expenseId: string) => travelApi.deleteTravelExpense(expenseId),
    onSuccess: () => invalidateAll(),
  });

  const estimateBudgetMut = useMutation({
    mutationFn: (
      input: {
        cabin?: 'economy' | 'premium_economy' | 'business' | 'first';
        travelers?: number;
      } = {},
    ) => travelApi.estimateTravelBudget(tripId!, input),
    onSuccess: () => invalidateAll(),
  });

  return {
    featureEnabled: feature.enabled,
    budget: (budgetQuery.data?.budget ?? null) as TravelBudgetResponse | null,
    expenses: (expensesQuery.data?.expenses ?? []) as TravelExpenseResponse[],
    summary: (summaryQuery.data?.summary ?? null) as TravelBudgetSummaryResponse | null,
    loading: budgetQuery.isLoading || expensesQuery.isLoading || summaryQuery.isLoading,
    error:
      (budgetQuery.error as Error | null)?.message ??
      (expensesQuery.error as Error | null)?.message ??
      (summaryQuery.error as Error | null)?.message ??
      null,
    refresh: () => {
      void budgetQuery.refetch();
      void expensesQuery.refetch();
      void summaryQuery.refetch();
    },
    updateBudget: (input: { homeCurrency?: string; totalCents?: number | null }) =>
      updateBudgetMut.mutateAsync(input),
    createExpense: (input: CreateExpenseVars) => createExpenseMut.mutateAsync(input),
    updateExpense: (expenseId: string, input: Partial<CreateExpenseVars>) =>
      updateExpenseMut.mutateAsync({ expenseId, input }),
    deleteExpense: (expenseId: string) => deleteExpenseMut.mutateAsync(expenseId),
    estimateBudget: (input?: {
      cabin?: 'economy' | 'premium_economy' | 'business' | 'first';
      travelers?: number;
    }) => estimateBudgetMut.mutateAsync(input ?? {}),
    isUpdatingBudget: updateBudgetMut.isPending,
    isCreatingExpense: createExpenseMut.isPending,
    isEstimatingBudget: estimateBudgetMut.isPending,
  };
}

/**
 * Formats integer cents + ISO 4217 code into a localized currency string.
 * `null`/NaN -> "—".
 */
export function formatMoney(cents: number | null | undefined, currency: string): string {
  if (cents == null || Number.isNaN(cents)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
